import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share, Plus } from "lucide-react";

const DISMISS_KEY = "fg_install_dismissed";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = window.navigator.userAgent || "";
    const isIos = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);

    // Android / Chrome: capture the native install prompt
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari: no auto prompt — show a one-time hint
    if (isIos && isSafari) {
      const t = setTimeout(() => setIosHint(true), 2500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBIP); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* */ }
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!show && !iosHint) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1600] w-[92%] max-w-md px-1" data-testid="install-prompt">
      <div className="bg-[hsl(var(--card))] border border-border rounded-2xl shadow-lg p-3 flex items-center gap-3">
        <img src="/icon-192.png" alt="Farm Gate" className="h-11 w-11 rounded-xl shrink-0" />
        {show ? (
          <>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight">Install Farm Gate</div>
              <div className="text-xs text-muted-foreground">Add to your home screen for quick access.</div>
            </div>
            <Button size="sm" onClick={install} data-testid="install-accept">
              <Download className="h-4 w-4 mr-1" />Install
            </Button>
            <button onClick={dismiss} className="p-1 text-muted-foreground" data-testid="install-dismiss" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold leading-tight">Install Farm Gate</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                Tap <Share className="h-3 w-3 inline" /> Share then <Plus className="h-3 w-3 inline" /> Add to Home Screen.
              </div>
            </div>
            <button onClick={dismiss} className="p-1 text-muted-foreground" data-testid="install-dismiss" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
